import { Outlet } from "react-router"
import * as Icons from "@nova/component-icons"
import { ProfileSettingsPage } from "features/data-management/workspace/workspace/body/settings-and-collaborators/profile/ProfileSettings"
import { PasswordSettingsPage } from "features/data-management/workspace/workspace/body/settings-and-collaborators/password/PasswordSettings"
import { NotificationSettingsPage } from "panels/notifications/settings/NotificationSettingsPage"
import {
    ApiSettingsPage,
    ApiSettingsRedirect,
    ApiTokenTabPage,
    ApiDocsTabPage,
    ApiExploreTabPage,
} from "features/data-management/workspace/workspace/body/settings-and-collaborators/api/ApiSettings"
import {
    GlobalNavNestedSidebar,
    GlobalNavNestedSidebarItem,
    GlobalNavNestedSidebarSection,
} from "panels/global-nav/GlobalNavNestedSidebar"
import { UsersSettingsPage } from "features/data-management/workspace/workspace/body/settings-and-collaborators/users/UsersSettings"
import { ButtonTertiary } from "@nova/component-btn"
import { ConfigurationSettingsPage } from "features/data-management/workspace/workspace/body/settings-and-collaborators/configuration/ConfigurationSettings"
import { IntegrationsSettingsPage } from "features/data-management/workspace/workspace/body/settings-and-collaborators/integrations/IntegrationSettings"
import { LicensesAndAddOnsSettingsPage } from "features/data-management/workspace/workspace/body/settings-and-collaborators/licenses/LicensesSettings"
import { RouteNode } from "@nova/react-router"
import { useEffect } from "react"
import { selectDefaultWorkspaceId } from "features/data-management/workspace/context/selectors"
import { SystemInitializingContainer } from "themes/default-tailwind/panels"
import { ConnectedAccountsSettingsPage } from "../../features/data-management/workspace/workspace/body/settings-and-collaborators/pilot-tracking/ConnectedAccountsSettings"
import {
    AllStreamsLinkRedirect,
    AllStreamsLinkSettingsPage,
} from "features/data-management/workspace/workspace/body/settings-and-collaborators/integrations/liveStream/LiveStreamIntegrationUI"
import {
    MissionGroupsSettingsPage,
    MissionGroupsRedirect,
    MissionGroupsSettingsGroupsTabPage,
    MissionGroupsSettingsSavedContactsTabPage,
} from "features/data-management/workspace/workspace/body/settings-and-collaborators/mission-groups/MissionGroupsSettings"
import { useRouter } from "pages"
import { useAppSelector } from "integrations/redux/hooks"

export function SettingsWrapper() {
    const router = useRouter("/workspace/:workspaceId")
    return (
        <div className="flex h-full">
            <GlobalNavNestedSidebar title="Settings">
                <div className="flex flex-col gap-2 p-3">
                    <GlobalNavNestedSidebarSection title="ACCOUNT">
                        <GlobalNavNestedSidebarItem
                            to={{ path: "/settings" }}
                            Icon={Icons.AccountCircle}
                            title="Profile"
                        />
                        <GlobalNavNestedSidebarItem
                            to={{ path: "/settings/notifications" }}
                            Icon={Icons.Notification}
                            title="Notifications"
                        />
                        <GlobalNavNestedSidebarItem
                            to={{ path: "/settings/password" }}
                            Icon={Icons.Password}
                            title="Password"
                        />
                    </GlobalNavNestedSidebarSection>
                    <GlobalNavNestedSidebarSection title="WORKSPACE">
                        <GlobalNavNestedSidebarItem
                            to={{ path: "/settings/users" }}
                            Icon={Icons.Collaborator}
                            title="Users"
                        />
                        <GlobalNavNestedSidebarItem
                            to={{ path: "/settings/configuration" }}
                            Icon={Icons.TVOptionsInputSettings}
                            title="Configuration"
                        />
                        <GlobalNavNestedSidebarItem
                            to={{ path: "/settings/integrations" }}
                            Icon={Icons.IntegrationInstructions}
                            title="Integrations"
                        />
                        <GlobalNavNestedSidebarItem
                            to={{ path: "/settings/api" }}
                            Icon={Icons.Key}
                            title="Processing API"
                            subRoutes={[
                                router.preview({ path: "/settings/api/token" }),
                                router.preview({ path: "/settings/api/docs" }),
                                router.preview({ path: "/settings/api/explore" }),
                            ]}
                        />
                    </GlobalNavNestedSidebarSection>
                    <GlobalNavNestedSidebarSection title="LIVE STREAM">
                        <GlobalNavNestedSidebarItem
                            to={{ path: "/settings/mission-groups" }}
                            Icon={Icons.ContactEmergency}
                            title="Mission Groups"
                            subRoutes={[
                                router.preview({ path: "/settings/mission-groups/groups" }),
                                router.preview({ path: "/settings/mission-groups/saved-contacts" }),
                            ]}
                        />
                        <GlobalNavNestedSidebarItem
                            to={{ path: "/settings/all-streams-link" }}
                            Icon={Icons.LiveVideoPlugin}
                            title="All Streams Link"
                        />
                    </GlobalNavNestedSidebarSection>
                    <GlobalNavNestedSidebarSection title="PILOT TRACKING">
                        <GlobalNavNestedSidebarItem
                            to={{ path: "/settings/connected-accounts" }}
                            Icon={Icons.AccountTree}
                            title="Connected Accounts"
                        />
                    </GlobalNavNestedSidebarSection>
                    <GlobalNavNestedSidebarSection title="MY SUBSCRIPTIONS">
                        <GlobalNavNestedSidebarItem
                            to={{ path: "/settings/licenses" }}
                            Icon={Icons.IdCard}
                            title="Licenses & Add-ons"
                        />
                    </GlobalNavNestedSidebarSection>
                </div>
            </GlobalNavNestedSidebar>
            <div className="grow flex justify-center">
                <Outlet />
            </div>
        </div>
    )
}

export function SettingsPasswordRedirect() {
    const router = useRouter("/")
    const workspaceId = useAppSelector((state) =>
        selectDefaultWorkspaceId(state, localStorage.getItem("recentWorkspace"))
    )
    useEffect(() => {
        if (!workspaceId) return
        router.navigate(
            { path: "/workspace/:workspaceId/settings/password", params: { workspaceId } },
            { replace: true }
        )
    }, [router, workspaceId])
    return <SystemInitializingContainer />
}

export function SupportButton() {
    return (
        <ButtonTertiary
            LeftIcon={Icons.Mail}
            title="Contact Support"
            onClick={() => window.open("mailto:help@mapnova.com", "_blank")}
        />
    )
}

export const SettingsRouteNode = {
    component: SettingsWrapper,
    index: ProfileSettingsPage,
    children: {
        notifications: { component: NotificationSettingsPage },
        password: { component: PasswordSettingsPage },
        api: {
            component: ApiSettingsPage,
            index: ApiSettingsRedirect,
            children: {
                token: { component: ApiTokenTabPage },
                docs: { component: ApiDocsTabPage },
                explore: { component: ApiExploreTabPage },
            },
        },
        users: { component: UsersSettingsPage },
        "contact-lists": { component: MissionGroupsRedirect },
        "mission-groups": {
            component: MissionGroupsSettingsPage,
            index: MissionGroupsRedirect,
            children: {
                groups: {
                    component: MissionGroupsSettingsGroupsTabPage,
                },
                "saved-contacts": {
                    component: MissionGroupsSettingsSavedContactsTabPage,
                },
            },
        },
        configuration: { component: ConfigurationSettingsPage },
        integrations: { component: IntegrationsSettingsPage },
        "live-stream-links": { component: AllStreamsLinkRedirect },
        "all-streams-link": { component: AllStreamsLinkSettingsPage },
        licenses: { component: LicensesAndAddOnsSettingsPage },
        "connected-accounts": { component: ConnectedAccountsSettingsPage },
    },
} satisfies RouteNode
